// components/finance/AssignCategoryDialog.tsx
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Stack,
  Divider,
  Box,
  Chip,
} from "@mui/material";
import { IBankTransaction, ITransactionCategory } from "../../@types/finance";
import { selectableTransactionCategories } from "../../utils/financeCategories";
import { fCurrency } from "../../utils/formatNumber";
import { fDate } from "../../utils/formatTime";

interface Props {
  open: boolean;
  transaction: IBankTransaction | null;
  categories: ITransactionCategory[];
  onClose: () => void;
  onSave: (txId: string, categoryId: string, reason: string) => void;
}

export default function AssignCategoryDialog({
  open,
  transaction,
  categories,
  onClose,
  onSave,
}: Props) {
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!transaction || !selectedCategoryId) return;
    setSaving(true);
    await onSave(transaction.id.toString(), selectedCategoryId, reason);
    setSaving(false);
    setSelectedCategoryId("");
    setReason("");
  };

  const handleClose = () => {
    setSelectedCategoryId("");
    setReason("");
    onClose();
  };

  // Sales-only categories (in-app dispense payments) are never valid for a bank
  // transaction — that activity is recorded against the sale in Dispense.
  const assignable = selectableTransactionCategories(categories);
  const incomeCategories = assignable.filter((c) => c.type === "income");
  const expenditureCategories = assignable.filter(
    (c) => c.type === "expenditure",
  );
  const financeCategories = assignable.filter((c) => c.type === "finance");

  if (!transaction) return null;

  const isCredit = transaction.direction === "credit";

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Category</DialogTitle>

      <DialogContent dividers>
        {/* Transaction summary */}
        <Box
          sx={{
            p: 1.5,
            bgcolor: "grey.50",
            borderRadius: 1,
            mb: 2.5,
            border: "1px solid",
            borderColor: "grey.200",
          }}>
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              {fDate(transaction.transactionDate)} ·{" "}
              {transaction.transactionType}
            </Typography>
            <Typography variant="subtitle2">{transaction.narrative}</Typography>
            {transaction.counterparty && (
              <Typography variant="caption" color="text.disabled">
                {transaction.counterparty}
              </Typography>
            )}
            <Typography
              variant="h6"
              fontFamily="monospace"
              color={isCredit ? "success.dark" : "error.dark"}>
              {isCredit ? "+" : "−"}
              {fCurrency(Math.abs(transaction.amount))}
            </Typography>
          </Stack>
        </Box>

        {/* Category selector — grouped */}
        <TextField
          select
          fullWidth
          label="Category"
          value={selectedCategoryId}
          onChange={(e) => setSelectedCategoryId(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          // displayEmpty renders the placeholder item inside the input while the
          // value is still "", so the label must be pinned shrunk or it paints
          // on top of the placeholder text.
          InputLabelProps={{ shrink: true }}
          SelectProps={{ displayEmpty: true }}>
          <MenuItem value="" disabled>
            <Typography color="text.disabled">Select a category…</Typography>
          </MenuItem>

          <MenuItem
            disabled
            sx={{
              fontSize: 11,
              color: "success.main",
              fontWeight: 700,
              mt: 0.5,
            }}>
            INCOME
          </MenuItem>
          {incomeCategories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                  }}
                />
                <span>{c.name}</span>
                {c.vatCode && (
                  <Chip
                    label={c.vatCode}
                    size="small"
                    sx={{ height: 16, fontSize: 10, ml: "auto" }}
                  />
                )}
              </Stack>
            </MenuItem>
          ))}

          <MenuItem
            disabled
            sx={{
              fontSize: 11,
              color: "error.main",
              fontWeight: 700,
              mt: 0.5,
            }}>
            EXPENDITURE
          </MenuItem>
          {expenditureCategories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "error.main",
                  }}
                />
                <span>{c.name}</span>
                {c.vatCode && (
                  <Chip
                    label={c.vatCode}
                    size="small"
                    sx={{ height: 16, fontSize: 10, ml: "auto" }}
                  />
                )}
              </Stack>
            </MenuItem>
          ))}

          <MenuItem
            disabled
            sx={{
              fontSize: 11,
              color: "secondary.main",
              fontWeight: 700,
              mt: 0.5,
            }}>
            FINANCE
          </MenuItem>
          {financeCategories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "secondary.main",
                  }}
                />
                <span>{c.name}</span>
                {c.vatCode && (
                  <Chip
                    label={c.vatCode}
                    size="small"
                    sx={{ height: 16, fontSize: 10, ml: "auto" }}
                  />
                )}
              </Stack>
            </MenuItem>
          ))}
        </TextField>

        {/* Reason (required if overriding) */}
        <TextField
          fullWidth
          label="Reason for override (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          rows={2}
          placeholder="e.g. Reclassified from insurance to marketing"
          helperText="This will be saved in the audit log."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedCategoryId || saving}>
          {saving ? "Saving…" : "Assign Category"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
